import {
  Formik,
  FormikConfig,
  FormikErrors,
  FormikValues,
  yupToFormErrors,
  FormikTouched,
} from 'formik';
import React, { useRef } from 'react';
import { ObjectSchema } from 'yup';
import { pickBy, mapObjIndexed } from 'ramda';
import isPresent from 'utils/isPresent';

interface Config<Values extends FormikValues> extends FormikConfig<Values> {
  validationSchema?: ObjectSchema<Values>;
  validate?: (values: Values) => FormikErrors<Values>; // disallow async validation
}

function setInitiallyTouchedFromErrors<Values>(
  errors: FormikErrors<Values>,
  initialValues: Values,
): FormikTouched<Values> {
  const hasInvalidInitialValue = mapObjIndexed((value, key) => {
    // Re-cast value and key be compatible with the recursive call
    const k = key as keyof Values;
    const v = value as FormikErrors<Values>[typeof k];
    if (!value) {
      return false;
    }
    if (typeof v === 'object') {
      return setInitiallyTouchedFromErrors(
        v as FormikErrors<Values[typeof k]>,
        initialValues[k],
      );
    } else {
      return Boolean(value && initialValues[k]);
    }
  }, errors);
  return hasInvalidInitialValue as FormikTouched<Values>;
}

/**
 * Wrap Formik to make setting initialErrors more convenient. If validation (by
 * either schema or function) is enabled and `initialErrors` is undefined, the
 * validation will be run and pass to Formik any errors as the `initialErrors`
 * prop.
 *
 * This only work if validation can by synchronously.
 */
const FmcForm = <Values extends FormikValues = FormikValues, ExtraProps = {}>(
  props: Config<Values> & ExtraProps,
): JSX.Element => {
  const { initialErrors, initialTouched, validate, validationSchema } = props;

  let { initialValues } = props;

  const errors = useRef(initialErrors);
  const touched = useRef(initialTouched);
  const initialErrorsSet = useRef<boolean>(Boolean(initialErrors));

  if (
    (validate || validationSchema) &&
    errors.current === undefined &&
    !initialErrorsSet.current
  ) {
    if (validate) {
      errors.current = validate(initialValues);
    } else if (validationSchema) {
      try {
        initialValues = {
          ...initialValues,
          ...validationSchema.cast(pickBy(isPresent, initialValues)),
        };
        validationSchema.validateSync(initialValues, { abortEarly: false });
      } catch (e) {
        if (e.name !== 'ValidationError') {
          throw e;
        }
        errors.current = yupToFormErrors(e);
      }
    }
    if (errors.current) {
      touched.current = setInitiallyTouchedFromErrors(
        errors.current,
        initialValues,
      );
    }
    initialErrorsSet.current = true;
  }

  return (
    <Formik
      {...props}
      initialValues={initialValues}
      initialErrors={errors.current}
      initialTouched={touched.current}
    />
  );
};

export default FmcForm;
